import UnicornApp from '../app';
import UnicornHttp from './http';
import UnicornStack from './stack';
import { defaultsDeep } from 'lodash-es';
let imported = false;
export default class UnicornTinymce {
    app;
    ui;
    instances = {};
    hooks = [];
    static install(app) {
        app.$ui.tinymce = new this(app, app.$ui);
    }
    constructor(app, ui) {
        this.app = app;
        this.ui = ui;
        //
    }
    async loadTinymce() {
        let tinymce = await this.app.import('@tinymce');
        if (imported) {
            return tinymce;
        }
        imported = true;
        for (const hook of this.hooks) {
            hook(tinymce);
        }
        await registerDragPlugin();
        return await tinymce;
    }
    configure(callback) {
        this.hooks.push(callback);
        return this;
    }
    async init(selector, options = {}) {
        await this.loadTinymce();
        return this.instances[selector] = this.create(document.querySelector(selector), options);
    }
    get(selector) {
        return this.instances[selector];
    }
    create(ele, options = {}) {
        return new TinymceEditor(this.app, ele, options);
    }
}
const defaultOptions = {};
export class TinymceEditor {
    app;
    element;
    editor;
    options = {};
    constructor(app, element, options) {
        this.app = app;
        this.element = element;
        options.target = element;
        this.options = defaultsDeep({}, defaultOptions, this.prepareOptions(options, tinymce.majorVersion), {
            unicorn: {
                stack_name: 'uploading'
            }
        });
        tinymce.EditorManager.init(this.options).then((editor) => {
            this.editor = editor[0];
        });
    }
    get $http() {
        return this.app.inject('$http');
    }
    getEditor() {
        return this.editor;
    }
    prepareOptions(options, version = '6') {
        const defaults = {};
        if (options.images_upload_url) {
            defaults.paste_data_images = true;
            defaults.remove_script_host = false;
            defaults.relative_urls = false;
            if (Number(version) >= 6) {
                defaults.images_upload_handler = (blobInfo, progress) => this.imageUploadHandler(blobInfo, progress);
            }
            else {
                options.plugins.push('paste');
                // @ts-ignore
                defaults.images_upload_handler = (blobInfo, success, failure, progress) => this.imageUploadHandler(blobInfo, progress)
                    .then((url) => {
                    success(url);
                    return url;
                })
                    .catch((e) => {
                    failure(e.message, { remove: true });
                    throw e;
                });
            }
        }
        // defaults.file_picker_callback = (...args) => this.filePickerCallback(...args);
        defaults.plugins = defaults.plugins || [];
        defaults.setup = (editor) => {
            editor.on('change', () => {
                tinymce.triggerSave();
            });
        };
        options = defaultsDeep({}, options, defaults);
        if (options.plugins.indexOf('unicorndragdrop') === -1) {
            options.plugins.push('unicorndragdrop');
        }
        return options;
    }
    insert(text) {
        this.editor?.insertContent(text);
    }
    getValue() {
        return this.editor?.getContent() ?? '';
    }
    setValue(text) {
        return this.editor?.setContent(text) ?? '';
    }
    // filePickerCallback(callback, value, meta) {
    //   const input = document.createElement('input');
    //   input.setAttribute('type', 'file');
    //   input.style.display = 'none';
    //
    //   if (meta.filetype === 'image') {
    //     input.setAttribute('accept', `image/\*`);
    //   }
    //
    //   document.body.appendChild(input);
    //
    //   input.onchange = function () {
    //     const file = this.files[0];
    //
    //     const reader = new FileReader();
    //     reader.onload = function () {
    //       const id = 'blobid' + (new Date()).getTime();
    //       const blobCache =  tinymce.activeEditor.editorUpload.blobCache;
    //       const base64 = reader.result.split(',')[1];
    //       const blobInfo = blobCache.create(id, file, base64);
    //       blobCache.add(blobInfo);
    //
    //       /* call the callback and populate the Title field with the file name */
    //       callback(blobInfo.blobUri(), { title: file.name, text: file.name });
    //     };
    //     reader.readAsDataURL(file);
    //     input.remove();
    //   };
    //
    //   input.click();
    // }
    async imageUploadHandler(blobInfo, progress) {
        const element = this.element;
        element.dispatchEvent(new CustomEvent('upload-start'));
        const formData = new FormData();
        formData.append('file', blobInfo.blob(), blobInfo.filename());
        const stack = this.app.inject('$stack').get(this.options.unicorn.stack_name);
        stack.push(true);
        try {
            let res = await this.$http.post(this.options.images_upload_url, formData, {
                withCredentials: false,
                onUploadProgress: (e) => {
                    progress(e.loaded / e.total * 100);
                }
            });
            element.dispatchEvent(new CustomEvent('upload-success'));
            return res.data.data.url;
        }
        catch (err) {
            const AxiosError = await this.app.inject('$http').errorClass();
            if (err instanceof AxiosError) {
                const message = err?.response?.data?.message || err.message;
                console.error(err?.response?.data?.message || err.message, err);
                element.dispatchEvent(new CustomEvent('upload-error', { detail: err }));
                return Promise.reject({ message, remove: true });
            }
            throw err;
        }
        finally {
            element.dispatchEvent(new CustomEvent('upload-complete'));
            stack.pop();
        }
    }
}
function registerDragPlugin() {
    tinymce.PluginManager.add('unicorndragdrop', function (editor) {
        // Reset the drop area border
        tinymce.DOM.bind(document, 'dragleave', function (e) {
            e.stopPropagation();
            e.preventDefault();
            if (tinymce.activeEditor) {
                tinymce.activeEditor.contentAreaContainer.style.transition = 'all .3s';
                tinymce.activeEditor.contentAreaContainer.style.borderWidth = '';
            }
            return false;
        });
        if (typeof FormData !== 'undefined') {
            // Fix for Chrome
            editor.on('dragenter', e => {
                e.stopPropagation();
                return false;
            });
            // Notify user when file is over the drop area
            editor.on('dragover', e => {
                e.preventDefault();
                if (tinymce.activeEditor) {
                    tinymce.activeEditor.contentAreaContainer.style.transition = 'all .3s';
                    tinymce.activeEditor.contentAreaContainer.style.border = '3px dashed rgba(0, 0, 0, .35)';
                }
                return false;
            });
            editor.on('drop', e => {
                editor.contentAreaContainer.style.borderWidth = '';
                editor.contentAreaContainer.style.borderWidth = '';
            });
        }
    });
    return Promise.resolve();
}
